// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';

// Utils
import {
    getFirstSelectedSecondaryColor,
    getSelectedElementColorStatusClass,
    getSelectedElementType,
} from '../colorUtils';
import { prop } from '../../../../../../common/utils/immutableHelper';
import { elementAllowsColorCombos } from '../../../../../element/board/colorComboPaletteUtils';

// Selectors
import { getThemeIsDarkMode } from '../../../../../user/account/accountModal/interface/themeSettings/themeSelector';

// Components
import ColorButton from './ColorButton';
import CustomColorPickerContainer from './CustomColorPickerContainer';
import ColorPopupModeSwitcher from './ColorPopupModeSwitcher';

// Styles
import './ColorPopupContent.scss';

// Constants
import { COLORS } from '../../../../../../common/colors/colorConstants';

const COLORS_ARRAY = Object.values(COLORS);

const colorPopupSelector = createStructuredSelector({
    isDarkModeTheme: getThemeIsDarkMode,
});

/**
 * This is being moved to its own component so that it does not render unless the popup is open.
 */
const ColorPopupContent = ({
    supportBackgroundColor,
    supportColorBar,
    setColor,
    defaultColors = COLORS_ARRAY,
    selectedElements,
    selectedColor,
    suggestedColors = [],
    colorPopupMode,
    closePopup,
    openCustomColorPicker,
    setColorPopupMode,
    isDarkModeTheme,
    colorComboList,
    supportColorCombos,
}) => {
    const selectedType = getSelectedElementType(selectedElements);
    const selectedStatus = getSelectedElementColorStatusClass(selectedElements);
    const secondaryColor = getFirstSelectedSecondaryColor(selectedElements);

    return (
        <div className="ColorPopupContent">
            {supportBackgroundColor && supportColorBar && (
                <ColorPopupModeSwitcher colorPopupMode={colorPopupMode} setColorPopupMode={setColorPopupMode} />
            )}

            <ul className="colors">
                {defaultColors.map((color) => (
                    <li key={color.name}>
                        <ColorButton
                            selectedType={selectedType}
                            selectedStatus={selectedStatus}
                            setColor={setColor}
                            color={color}
                            colorPopupMode={colorPopupMode}
                            isActive={selectedColor === color.css || selectedColor === color.name}
                            className={color.className}
                            isDarkModeTheme={isDarkModeTheme}
                        />
                    </li>
                ))}
            </ul>

            {supportColorCombos && (
                <>
                    <div className="divider" />
                    <ul className="colors">
                        {colorComboList.map((color) => {
                            const primary = prop('primary', color);
                            const secondary = prop('secondary', color);
                            return (
                                <li key={color}>
                                    <ColorButton
                                        selectedType={selectedType}
                                        selectedStatus={selectedStatus}
                                        setColor={setColor}
                                        color={primary}
                                        secondaryColor={secondary}
                                        colorPopupMode={colorPopupMode}
                                        isActive={selectedColor === primary && secondaryColor === secondary}
                                        isDarkModeTheme={isDarkModeTheme}
                                        allowColorCombos={elementAllowsColorCombos(selectedElements.first())}
                                    />
                                </li>
                            );
                        })}
                    </ul>
                </>
            )}

            {suggestedColors.length > 0 && (
                <>
                    <div className="divider" />

                    <ul className="colors">
                        {suggestedColors.map((color) => (
                            <li key={color}>
                                <ColorButton
                                    selectedType={selectedType}
                                    selectedStatus={selectedStatus}
                                    setColor={setColor}
                                    color={color}
                                    colorPopupMode={colorPopupMode}
                                    isActive={selectedColor === color && !secondaryColor}
                                    className={color.className}
                                    isDarkModeTheme={isDarkModeTheme}
                                />
                            </li>
                        ))}
                    </ul>
                </>
            )}

            <CustomColorPickerContainer closePopup={closePopup} openCustomColorPicker={openCustomColorPicker} />
        </div>
    );
};

ColorPopupContent.propTypes = {
    supportBackgroundColor: PropTypes.bool,
    supportColorBar: PropTypes.bool,
    isDarkModeTheme: PropTypes.bool,
    colorPopupMode: PropTypes.string,
    openCustomColorPicker: PropTypes.func,
    selectedElements: PropTypes.object,
    suggestedColors: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    colorComboList: PropTypes.object,
    defaultColors: PropTypes.array,
    setColorPopupMode: PropTypes.func,
    setColor: PropTypes.func,
    closePopup: PropTypes.func,
    selectedColor: PropTypes.string,
    elementType: PropTypes.string,
    supportColorCombos: PropTypes.bool,
};

export default connect(colorPopupSelector)(ColorPopupContent);
